import React, { useEffect, useState } from 'react';
import { Calendar, DollarSign, Clock, Calculator, Save, AlertCircle } from 'lucide-react';
export function DailyOpsView() {
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  // Sales State
  const [sales, setSales] = useState({
    food: 0,
    bev: 0,
    misc: 0,
    ccSales: 0,
    hseChrg: 0,
    adjCC: 0,
    cash: 0,
    tips: 0,
    voids: 0
  });
  // Labor State
  const [labor, setLabor] = useState({
    fohHours: 0,
    fohRate: 22,
    bohHours: 0,
    bohRate: 25,
    modSalary: 250,
    kitchenHours: 0,
    kitchenRate: 30
  });
  // Derived Calcs
  const grossSales = sales.food + sales.bev + sales.misc;
  const processingFee = sales.ccSales * 0.03;
  const taxes = grossSales * 0.08; // 8% avg
  const netTotal = grossSales - processingFee - taxes - sales.voids;
  const fohTotal = labor.fohHours * labor.fohRate;
  const bohTotal = labor.bohHours * labor.bohRate;
  const kitchenTotal = labor.kitchenRate / 50 * labor.kitchenHours;
  const totalLabor = fohTotal + bohTotal + labor.modSalary + kitchenTotal;
  const laborPercent = grossSales > 0 ? totalLabor / grossSales * 100 : 0;
  const handleSalesChange = (field: string, val: string) => {
    setSales(prev => ({
      ...prev,
      [field]: parseFloat(val) || 0
    }));
  };
  const handleLaborChange = (field: string, val: string) => {
    setLabor(prev => ({
      ...prev,
      [field]: parseFloat(val) || 0
    }));
  };
  return <div className="space-y-6" data-id="element-39">
      <div className="flex items-center justify-between" data-id="element-40">
        <div data-id="element-41">
          <h2 className="text-2xl font-bold text-slate-900" data-id="element-42">
            Daily Operations
          </h2>
          <p className="text-sm text-slate-500" data-id="element-43">
            Sales & Labor Entry for Closeout
          </p>
        </div>
        <div className="flex items-center gap-3" data-id="element-44">
          <div className="relative" data-id="element-45">
            <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} data-id="element-46" />
            <input type="date" value={date} onChange={e => setDate(e.target.value)} className="pl-10 pr-4 py-2 bg-white border border-slate-200 rounded-xl text-sm font-bold focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 outline-none transition-all" data-id="element-47" />
          </div>
          <button className="flex items-center gap-2 px-6 py-2 bg-emerald-500 text-white rounded-xl text-sm font-bold shadow-lg shadow-emerald-500/20 hover:bg-emerald-600 transition-all" data-id="element-48">
            <Save size={16} data-id="element-49" />
            Submit Daily Closeout
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-50">
        {/* Sales Section */}
        <div className="lg:col-span-2 space-y-6" data-id="element-51">
          <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm" data-id="element-52">
            <div className="flex items-center gap-2 mb-6" data-id="element-53">
              <DollarSign className="text-emerald-500" size={20} data-id="element-54" />
              <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wider" data-id="element-55">
                Sales Entry
              </h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-56">
              <InputGroup label="Food Sales ($)" value={sales.food} onChange={v => handleSalesChange('food', v)} data-id="element-57" />
              <InputGroup label="Beverage Sales ($)" value={sales.bev} onChange={v => handleSalesChange('bev', v)} data-id="element-58" />
              <InputGroup label="Late Night / Misc ($)" value={sales.misc} onChange={v => handleSalesChange('misc', v)} data-id="element-59" />
              <InputGroup label="Credit Card Sales" value={sales.ccSales} onChange={v => handleSalesChange('ccSales', v)} data-id="element-60" />
              <InputGroup label="HSE/Chrg (Weekly)" value={sales.hseChrg} onChange={v => handleSalesChange('hseChrg', v)} data-id="element-61" />
              <InputGroup label="Adj. CC Deposit" value={sales.adjCC} onChange={v => handleSalesChange('adjCC', v)} data-id="element-62" />
              <InputGroup label="Cash Sales" value={sales.cash} onChange={v => handleSalesChange('cash', v)} data-id="element-63" />
              <InputGroup label="Tips" value={sales.tips} onChange={v => handleSalesChange('tips', v)} data-id="element-64" />
              <InputGroup label="Voids/Comps" value={sales.voids} onChange={v => handleSalesChange('voids', v)} data-id="element-65" />
            </div>
          </div>

          <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm" data-id="element-66">
            <div className="flex items-center gap-2 mb-6" data-id="element-67">
              <Clock className="text-blue-500" size={20} data-id="element-68" />
              <h3 className="text-sm font-bold text-slate-900 uppercase tracking-wider" data-id="element-69">
                Labor Entry
              </h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-70">
              <div className="space-y-4" data-id="element-71">
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest" data-id="element-72">
                  Front of House (FOH)
                </p>
                <div className="grid grid-cols-2 gap-3" data-id="element-73">
                  <InputGroup label="Hours" value={labor.fohHours} onChange={v => handleLaborChange('fohHours', v)} data-id="element-74" />
                  <InputGroup label="Rate ($)" value={labor.fohRate} onChange={v => handleLaborChange('fohRate', v)} data-id="element-75" />
                </div>
              </div>
              <div className="space-y-4" data-id="element-76">
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest" data-id="element-77">
                  Back of House (BOH)
                </p>
                <div className="grid grid-cols-2 gap-3" data-id="element-78">
                  <InputGroup label="Hours" value={labor.bohHours} onChange={v => handleLaborChange('bohHours', v)} data-id="element-79" />
                  <InputGroup label="Rate ($)" value={labor.bohRate} onChange={v => handleLaborChange('bohRate', v)} data-id="element-80" />
                </div>
              </div>
              <div className="space-y-4" data-id="element-81">
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest" data-id="element-82">
                  Management & Kitchen
                </p>
                <div className="grid grid-cols-3 gap-3" data-id="element-83">
                  <InputGroup label="MOD Salary" value={labor.modSalary} onChange={v => handleLaborChange('modSalary', v)} data-id="element-84" />
                  <InputGroup label="Kitchen Hrs" value={labor.kitchenHours} onChange={v => handleLaborChange('kitchenHours', v)} data-id="element-85" />
                  <InputGroup label="Kitchen Rate" value={labor.kitchenRate} onChange={v => handleLaborChange('kitchenRate', v)} data-id="element-86" />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Summary Sidebar */}
        <div className="space-y-6" data-id="element-87">
          <div className="bg-slate-900 rounded-2xl p-6 text-white shadow-xl sticky top-6" data-id="element-88">
            <div className="flex items-center gap-2 mb-8" data-id="element-89">
              <Calculator className="text-emerald-400" size={20} data-id="element-90" />
              <h3 className="text-sm font-bold uppercase tracking-wider" data-id="element-91">
                Auto-Calculations
              </h3>
            </div>

            <div className="space-y-4" data-id="element-92">
              <CalcRow label="Gross Sales" value={`$${grossSales.toLocaleString()}`} highlight data-id="element-93" />
              <CalcRow label="Processing Fee (3%)" value={`-$${processingFee.toFixed(2)}`} data-id="element-94" />
              <CalcRow label="Estimated Taxes" value={`-$${taxes.toFixed(2)}`} data-id="element-95" />
              <CalcRow label="Voids/Comps" value={`-$${sales.voids.toFixed(2)}`} data-id="element-96" />
              <div className="pt-4 border-t border-white/10" data-id="element-97">
                <CalcRow label="Net Total" value={`$${netTotal.toLocaleString()}`} highlight emerald data-id="element-98" />
              </div>
            </div>

            <div className="mt-10 space-y-4" data-id="element-99">
              <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest" data-id="element-100">
                Labor Summary
              </p>
              <CalcRow label="Total Labor Cost" value={`$${totalLabor.toLocaleString()}`} data-id="element-101" />
              <CalcRow label="Labor % of Sales" value={`${laborPercent.toFixed(1)}%`} highlight blue data-id="element-102" />
              {laborPercent > 25 && <div className="flex items-start gap-2 p-3 bg-amber-500/10 rounded-xl border border-amber-500/20 mt-4" data-id="element-103">
                  <AlertCircle size={14} className="text-amber-400 mt-0.5 shrink-0" data-id="element-104" />
                  <p className="text-[10px] text-amber-200 leading-relaxed font-medium" data-id="element-105">
                    Labor cost is currently above the 20% target. Consider
                    adjusting MOD split or BOH hours.
                  </p>
                </div>}
            </div>
          </div>
        </div>
      </div>
    </div>;
}
function InputGroup({
  label,
  value,
  onChange
}: any) {
  return <div data-id="element-106">
      <label className="block text-[10px] font-bold text-slate-400 uppercase mb-1.5 ml-1" data-id="element-107">
        {label}
      </label>
      <input type="number" value={value || ''} onChange={e => onChange(e.target.value)} placeholder="0.00" className="w-full px-4 py-2.5 bg-slate-50 border border-slate-100 rounded-xl text-sm font-bold text-slate-900 focus:bg-white focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 outline-none transition-all" data-id="element-108" />
    </div>;
}
function CalcRow({
  label,
  value,
  highlight,
  emerald,
  blue
}: any) {
  return <div className="flex items-center justify-between" data-id="element-109">
      <span className="text-xs font-bold text-slate-400" data-id="element-110">{label}</span>
      <span className={`text-sm font-black ${highlight ? emerald ? 'text-emerald-400 text-lg' : blue ? 'text-blue-400 text-lg' : 'text-white' : 'text-slate-300'}`} data-id="element-111">
        {value}
      </span>
    </div>;
}